/**
 * Converts a decimal hours value into a human-readable "Xh Ym" format.
 * Minutes are not zero-padded ("8h 0m", not "8h 00m" — Timo, 02.11.2026);
 * otherwise matches the legacy `humanReadableHours()` PHP helper.
 *
 * @example humanReadableHours(7.5)   → "7h 30m"
 * @example humanReadableHours(-3.5)  → "-3h 30m"
 * @example humanReadableHours(0)     → "0h 0m"
 */
export const humanReadableHours = (hours: number): string => {
    const isNegative = hours < 0
    const abs = Math.abs(hours)
    const h = Math.floor(abs)
    const m = Math.round((abs - h) * 60)
    const sign = isNegative ? '-' : ''
    return `${sign}${h}h ${m}m`
}

/**
 * Converts an ISO date string (YYYY-MM-DD) to display format.
 *
 * @param format - 'short' (default): DD.MM.YYYY, 'long': human-readable "2. märts 2026"
 * @param lang - language for long format, defaults to 'et'
 *
 * @example formatDisplayDate('2026-04-06')                → '06.04.2026'
 * @example formatDisplayDate('2026-04-06', 'long')        → '6. aprill 2026'
 * @example formatDisplayDate('2026-04-06', 'long', 'en')  → '6 April 2026'
 */
export const formatDisplayDate = (
    isoDate: string,
    format: 'short' | 'long' = 'short',
    lang: 'et' | 'en' = 'et',
): string => {
    if (format === 'short') {
        const [year, month, day] = isoDate.split('-')
        return `${day}.${month}.${year}`
    }

    // Long human-readable format: "2. märts 2026" / "2 March 2026"
    const date = new Date(isoDate + 'T00:00:00')
    const locale = lang === 'et' ? 'et-EE' : 'en-GB'
    const day = date.getDate()
    const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
    const year = date.getFullYear()

    return lang === 'et' ? `${day}. ${month} ${year}` : `${day} ${month} ${year}`
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Whole calendar days from `today` (its UTC date) to the given ISO date
 * (YYYY-MM-DD), comparing both at UTC midnight so it's DST/timezone-stable.
 * Returns a positive number for future dates, negative for past, 0 for today,
 * and null when `iso` is empty or unparseable.
 *
 * @example daysUntilIso('2026-06-20', new Date('2026-06-16T09:00:00Z')) → 4
 */
export const daysUntilIso = (iso: string, today: Date = new Date()): number | null => {
    if (!iso) return null
    const target = new Date(iso + 'T00:00:00Z')
    if (isNaN(target.getTime())) return null
    const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
    return Math.round((target.getTime() - todayUtc) / MS_PER_DAY)
}

/**
 * FTE/workload fraction for display: Estonian uses a decimal comma.
 *
 * @example formatFte(0.8, 'et') → '0,8'
 * @example formatFte(0.8, 'en') → '0.8'
 * @example formatFte(1, 'et')   → '1'
 */
export const formatFte = (fte: number, lang: 'et' | 'en' = 'et'): string => {
    const s = String(fte)
    return lang === 'et' ? s.replace('.', ',') : s
}

/**
 * Workload (koormus) for the summary-table header: rounded to at most 3
 * decimals, always showing at least one so full-time reads "1,0" not "1".
 *
 * @example formatWorkload(1, 'et')      → '1,0'
 * @example formatWorkload(0.8, 'et')    → '0,8'
 * @example formatWorkload(0.6667, 'en') → '0.667'
 */
export const formatWorkload = (fte: number, lang: 'et' | 'en' = 'et'): string => {
    const rounded = Math.round(fte * 1000) / 1000
    const s = Number.isInteger(rounded) ? rounded.toFixed(1) : String(rounded)
    return lang === 'et' ? s.replace('.', ',') : s
}

/**
 * Groups a phone number for display: country code separated by a space, the
 * subscriber number split 4+4 (8 digits) or 3+4 (7 digits). Anything that
 * doesn't look like a 7/8-digit number (with optional country code) is
 * returned trimmed as-is. `tel:` hrefs must keep using the unspaced form.
 *
 * @example formatPhoneDisplay('+37251234567') → '+372 5123 4567'
 * @example formatPhoneDisplay('+3726123456')  → '+372 612 3456'
 * @example formatPhoneDisplay('5123 4567')    → '5123 4567'
 */
export const formatPhoneDisplay = (phone: string): string => {
    const cleaned = phone.trim().replace(/[\s-]/g, '')
    const match = cleaned.match(/^(\+\d{1,3})?(\d{7,8})$/)
    if (!match) return phone.trim()

    const [, countryCode, digits] = match
    const grouped =
        digits.length === 8
            ? `${digits.slice(0, 4)} ${digits.slice(4)}`
            : `${digits.slice(0, 3)} ${digits.slice(3)}`
    return countryCode ? `${countryCode} ${grouped}` : grouped
}

/**
 * Ensure a phone number carries a country code so `tel:` links dial correctly
 * outside Estonia: `00` international prefixes become `+`, bare local numbers
 * get +372, and anything already starting with `+` (or not looking like a
 * number at all) passes through untouched.
 *
 * @example withCountryCode('5123 4567')     → '+3725123 4567'
 * @example withCountryCode('+358 40 12345') → '+358 40 12345'
 */
export const withCountryCode = (phone: string, defaultCode = '+372'): string => {
    const trimmed = phone.trim()
    if (trimmed.startsWith('+')) return trimmed
    if (/^00\d/.test(trimmed)) return `+${trimmed.slice(2)}`
    return /^[\d\s-]+$/.test(trimmed) ? `${defaultCode}${trimmed}` : trimmed
}

export type NormalizedPhone = { ok: true; value: string } | { ok: false }

/**
 * Canonical stored form of a phone number (TASK-612): every number carries a
 * country code so it dials from abroad. Spaces and dashes are dropped, the
 * `00` international prefix becomes `+`, and a bare 7/8-digit number is taken
 * to be Estonian and gets +372. Anything else without a `+` (a foreign
 * number typed without its code, letters, extensions) is rejected so the
 * user adds the code explicitly. Empty input is valid — the field stays
 * optional, only the code is mandatory.
 *
 * @example normalizePhone('5123 4567')       → { ok: true, value: '+37251234567' }
 * @example normalizePhone('+358 40 1234567') → { ok: true, value: '+358401234567' }
 * @example normalizePhone('0401234567')      → { ok: false }
 */
export const normalizePhone = (input: string, defaultCode = '+372'): NormalizedPhone => {
    const compact = input.trim().replace(/[\s-]/g, '')
    if (compact === '') return { ok: true, value: '' }

    const withPlus = /^00\d/.test(compact) ? `+${compact.slice(2)}` : compact
    if (/^\+\d{7,15}$/.test(withPlus)) return { ok: true, value: withPlus }
    if (/^\d{7,8}$/.test(withPlus)) return { ok: true, value: `${defaultCode}${withPlus}` }
    return { ok: false }
}

const localeFor = (lang: 'et' | 'en'): string => (lang === 'et' ? 'et-EE' : 'en-GB')

const shortMonth = (date: Date, lang: 'et' | 'en'): string =>
    new Intl.DateTimeFormat(localeFor(lang), { month: 'short' }).format(date)

const pad2 = (n: number): string => String(n).padStart(2, '0')

/**
 * Harvest-style period title for the work-time view's period bar.
 *
 * @example formatPeriodTitle('month', '2026-05-01', '2026-05-31', 'et')   → 'Mai 2026'
 * @example formatPeriodTitle('year', '2026-01-01', '2026-12-31', 'et')    → '2026'
 * @example formatPeriodTitle('quarter', '2026-04-01', '2026-06-30', 'en') → '01 Apr – 30 Jun 2026'
 */
export const formatPeriodTitle = (
    rangeType: 'week' | 'month' | 'quarter' | 'year',
    startISO: string,
    endISO: string,
    lang: 'et' | 'en' = 'et',
): string => {
    const start = new Date(startISO + 'T00:00:00')
    const end = new Date(endISO + 'T00:00:00')

    if (rangeType === 'year') return String(start.getFullYear())

    if (rangeType === 'month') {
        const month = new Intl.DateTimeFormat(localeFor(lang), { month: 'long' }).format(start)
        const capitalized = month.charAt(0).toUpperCase() + month.slice(1)
        return `${capitalized} ${start.getFullYear()}`
    }

    // week / quarter: a date range, year shown once unless the range spans two
    const from = `${pad2(start.getDate())} ${shortMonth(start, lang)}`
    const to = `${pad2(end.getDate())} ${shortMonth(end, lang)}`
    if (start.getFullYear() !== end.getFullYear()) {
        return `${from} ${start.getFullYear()} – ${to} ${end.getFullYear()}`
    }
    return `${from} – ${to} ${end.getFullYear()}`
}

/**
 * Label for the period bar's middle "jump back to now" button. Returns null
 * while the displayed period contains today — the caller then shows the static
 * "See kuu" / "This week" text. Once the user pages away, the button names the
 * period on screen instead, so the bar never claims to show "this month" while
 * displaying another one. The year is appended only when it differs from
 * today's, keeping the common back-a-month case short.
 *
 * @example formatPeriodJumpLabel('month', '2026-06-01', '2026-06-30', '2026-07-02', 'et')   → 'Juuni'
 * @example formatPeriodJumpLabel('month', '2025-12-01', '2025-12-31', '2026-07-02', 'et')   → 'Detsember 2025'
 * @example formatPeriodJumpLabel('month', '2026-07-01', '2026-07-31', '2026-07-02', 'et')   → null
 * @example formatPeriodJumpLabel('quarter', '2026-01-01', '2026-03-31', '2026-07-02', 'et') → 'Q1'
 * @example formatPeriodJumpLabel('week', '2026-06-15', '2026-06-21', '2026-07-02', 'et')    → '15.06 – 21.06'
 */
export const formatPeriodJumpLabel = (
    rangeType: 'week' | 'month' | 'quarter' | 'year',
    startISO: string,
    endISO: string,
    todayISO: string,
    lang: 'et' | 'en' = 'et',
): string | null => {
    if (startISO <= todayISO && todayISO <= endISO) return null

    const start = new Date(startISO + 'T00:00:00')
    const end = new Date(endISO + 'T00:00:00')
    const todayYear = Number(todayISO.slice(0, 4))
    const yearSuffix = start.getFullYear() !== todayYear ? ` ${start.getFullYear()}` : ''

    if (rangeType === 'year') return String(start.getFullYear())

    if (rangeType === 'month') {
        const month = new Intl.DateTimeFormat(localeFor(lang), { month: 'long' }).format(start)
        return `${month.charAt(0).toUpperCase()}${month.slice(1)}${yearSuffix}`
    }

    if (rangeType === 'quarter') {
        return `Q${Math.floor(start.getMonth() / 3) + 1}${yearSuffix}`
    }

    // week: compact numeric range — the full title above the bar already spells
    // out the months.
    const from = `${pad2(start.getDate())}.${pad2(start.getMonth() + 1)}`
    const to = `${pad2(end.getDate())}.${pad2(end.getMonth() + 1)}`
    return `${from} – ${to}${yearSuffix}`
}
